import type { CSSProperties, FC, KeyboardEvent, PointerEvent } from 'react'
import type { ProjectGrowth } from '@/types'

import { useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { areaPath, monotonePath, scalePoints } from '@/lib/chart'
import { InView } from '@/components/kit/in-view'

const WIDTH = 600
const HEIGHT = 240

const niceCeiling = (value: number): number => {
    if (value <= 0) return 1

    const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
    const normalized = value / magnitude
    const step = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find(option => normalized <= option) ?? 10

    return step * magnitude
}

export const formatGrowthValue = (value: number, unit = ''): string => {
    if (unit === 'K' && value >= 1000) return `${(value / 1000).toFixed(1)}M`
    if (unit === 'x') return `${value.toFixed(1)}x`

    const decimals = Number.isInteger(value) ? 0 : 1

    return `${new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value)}${unit}`
}

/**
 * Single-series growth chart for a case study. Hover or use the arrow keys
 * to read each point; a table carries the same values for screen readers.
 */
export const GrowthChart: FC<{ growth: ProjectGrowth }> = ({ growth }) => {
    const { points: values, unit = '', label } = growth
    const labels = growth.labels?.length === values.length ? growth.labels : values.map((_, index) => `P${index + 1}`)
    const max = Math.max(...values)
    const ceiling = niceCeiling(max * 1.05)
    const points = useMemo(() => scalePoints([...values, ceiling], WIDTH, HEIGHT, { pad: 0, floor: 0 }).slice(0, values.length), [values, ceiling])
    const [active, setActive] = useState<number | null>(null)
    const plotRef = useRef<HTMLDivElement>(null)

    const xFor = (index: number) => (values.length > 1 ? (index / (values.length - 1)) * WIDTH : 0)
    const adjusted = points.map((point, index) => [xFor(index), point[1]] as [number, number])
    const shown = active ?? values.length - 1
    const point = adjusted[shown]
    const ticks = [0, 0.5, 1].map(ratio => ceiling * ratio)

    const onMove = (event: PointerEvent<HTMLDivElement>) => {
        const rect = plotRef.current?.getBoundingClientRect()

        if (!rect) return

        const ratio = (event.clientX - rect.left) / rect.width

        setActive(Math.min(values.length - 1, Math.max(0, Math.round(ratio * (values.length - 1)))))
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowRight') {
            event.preventDefault()
            setActive(current => Math.min(values.length - 1, (current ?? values.length - 1) + 1))
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault()
            setActive(current => Math.max(0, (current ?? values.length - 1) - 1))
        }
    }

    return (
        <InView as='figure'>
            <figcaption className='flex flex-wrap items-center justify-between gap-3'>
                <span className='font-heading text-2xl font-extrabold tracking-[-0.04em]'>{label}</span>
                <span className='inline-flex items-center gap-2 rounded-full border border-ink/10 px-3 py-1 font-mono text-[11px]'>
                    <span className='size-2 rounded-[2px] bg-volt' />
                    {label}{unit ? `, ${unit === 'x' ? 'multiple' : unit === 'K' ? 'thousands' : unit}` : ''}
                </span>
            </figcaption>

            <div className='mt-8 grid grid-cols-[auto_1fr] gap-x-3'>
                <div
                    aria-hidden='true'
                    className='relative h-60 w-12 font-mono text-[10px] text-stone md:h-72'
                >
                    {ticks.map(tick => (
                        <span
                            key={tick}
                            className='absolute right-0 translate-y-1/2'
                            style={{ bottom: `${(tick / ceiling) * 100}%` }}
                        >
                            {formatGrowthValue(Math.round(tick * 10) / 10, unit)}
                        </span>
                    ))}
                </div>

                <div
                    ref={plotRef}
                    tabIndex={0}
                    role='group'
                    aria-label={`${label} chart. Use the left and right arrow keys to read each value.`}
                    onPointerMove={onMove}
                    onPointerLeave={() => setActive(null)}
                    onKeyDown={onKeyDown}
                    onBlur={() => setActive(null)}
                    className='relative h-60 cursor-crosshair touch-pan-y rounded-lg md:h-72'
                >
                    <svg
                        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                        preserveAspectRatio='none'
                        aria-hidden='true'
                        className='absolute inset-0 h-full w-full overflow-visible'
                    >
                        <defs>
                            <linearGradient id='growth-fill' x1='0' x2='0' y1='0' y2='1'>
                                <stop offset='0%' stopColor='#0099ff' stopOpacity='0.2' />
                                <stop offset='100%' stopColor='#0099ff' stopOpacity='0' />
                            </linearGradient>
                        </defs>

                        {ticks.slice(1).map(tick => {
                            const y = HEIGHT - (tick / ceiling) * HEIGHT

                            return (
                                <line key={tick} x1='0' x2={WIDTH} y1={y} y2={y} stroke='rgb(30 30 30 / 0.07)' strokeWidth='1' vectorEffect='non-scaling-stroke' />
                            )
                        })}
                        <line x1='0' x2={WIDTH} y1={HEIGHT} y2={HEIGHT} stroke='rgb(30 30 30 / 0.22)' strokeWidth='1' vectorEffect='non-scaling-stroke' />

                        <path
                            className='pop-in'
                            d={areaPath(adjusted, HEIGHT)}
                            fill='url(#growth-fill)'
                            style={{ '--delay': '600ms' } as CSSProperties}
                        />
                        <path
                            className='draw-path'
                            pathLength={1}
                            d={monotonePath(adjusted)}
                            fill='none'
                            stroke='#0099ff'
                            strokeWidth='2'
                            strokeLinecap='round'
                            vectorEffect='non-scaling-stroke'
                            style={{ '--draw-delay': '200ms' } as CSSProperties}
                        />
                    </svg>

                    <span
                        className={cn('pointer-events-none absolute inset-y-0 w-px bg-ink/25 transition-opacity', active === null ? 'opacity-0' : 'opacity-100')}
                        style={{ left: `${(point[0] / WIDTH) * 100}%` }}
                    />

                    {adjusted.map((item, index) => (
                        <span
                            key={index}
                            className={cn(
                                'pointer-events-none absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-volt ring-2 ring-paper transition-transform duration-300',
                                index === shown ? 'scale-150' : 'scale-100'
                            )}
                            style={{ left: `${(item[0] / WIDTH) * 100}%`, top: `${(item[1] / HEIGHT) * 100}%` }}
                        />
                    ))}

                    <span
                        className='pointer-events-none absolute z-10 whitespace-nowrap rounded-lg bg-ink px-3 py-2 text-cream shadow-lg'
                        style={{
                            left: `${(point[0] / WIDTH) * 100}%`,
                            top: `${(point[1] / HEIGHT) * 100}%`,
                            transform: `translate(${shown >= values.length - 2 ? '-100%' : shown <= 1 ? '0%' : '-50%'}, calc(-100% - 14px))`
                        }}
                    >
                        <span className='block font-mono text-[10px] text-cream/60'>{labels[shown]}</span>
                        <span className='block font-heading text-lg font-extrabold leading-tight tracking-[-0.03em]'>{formatGrowthValue(values[shown], unit)}</span>
                    </span>
                </div>

                <span aria-hidden='true' />
                <div
                    aria-hidden='true'
                    className='mt-3 flex justify-between font-mono text-[10px] text-stone'
                >
                    {labels.map((item, index) => (
                        <span
                            key={`${item}-${index}`}
                            className={cn(values.length > 8 && index % 2 === 1 && 'hidden sm:inline')}
                        >
                            {item}
                        </span>
                    ))}
                </div>
            </div>

            <table className='sr-only'>
                <caption>{label}</caption>
                <thead>
                    <tr>
                        <th scope='col'>Period</th>
                        <th scope='col'>Value</th>
                    </tr>
                </thead>
                <tbody>
                    {values.map((value, index) => (
                        <tr key={index}>
                            <th scope='row'>{labels[index]}</th>
                            <td>{formatGrowthValue(value, unit)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </InView>
    )
}
